import { List } from "immutable";
import * as createjs from "@createjs/core";
import * as VoyageHistoryUtil from "@/utils/VoyageHistoryUtil";
import { createTrackFeatureCollection } from "@/utils/VoyageHistoryUtil";
import { AiseaApiFactory } from "@/api/aisea/apiFactory";
import * as VesselUtil from "@/utils/VesselUtil";
import * as turf from "@turf/turf";

export default {
  async getMyVesselHistory({ commit }, payload) {
    let range = {
      started_at: payload.startTime,
      ended_at: payload.endTime,
    };

    // 自船位置取得: GET /voyage_records/vessels/{id}
    let res = await AiseaApiFactory.get("enterpriseVoyageRecords").fetchVesselPoints(
      payload.vesselId,
      range,
    );
    if (!res.data) return false;
    if (res.data.points.length === 0) return false;

    const vessel = VoyageHistoryUtil.vessel(res.data);
    let myVessel = {};
    myVessel[payload.vesselId] = {
      ...vessel,
      isMyVessel: true,
    };
    commit("SET_MY_VESSEL_ID", payload.vesselId);
    commit("SET_VESSEL_MAP_OBJECTS", myVessel);

    let tracks = createTrackFeatureCollection(vessel.pointsArray);
    commit("SET_MY_TRACKS", tracks);

    let chunkPoints = VoyageHistoryUtil.createChunkPoints(
      vessel.pointsArray,
      4000,
      vessel.vesselId,
    );
    commit("SET_CHUNK_POINTS", chunkPoints);

    return true;
  },

  async getOtherVesselsHistory({ commit, state }, payload) {
    let chunkPoints = state.chunkPoints;
    const myVesselId = payload.myVesselId;

    for (const value of chunkPoints) {
      // 他船位置取得: GET /voyage_records/vessels/search
      let res = await AiseaApiFactory.get("enterpriseVoyageRecords").searchVesselsPoints(
        value,
      );

      for (const value of Object.values(res.data)) {
        const vessel = VoyageHistoryUtil.vessel(value);
        let copyVesselsArray = JSON.parse(JSON.stringify(state.vesselMapObjects));
        if (typeof copyVesselsArray[vessel.vesselId] === "undefined") {
          copyVesselsArray[vessel.vesselId] = {
            ...vessel,
            isMyVessel: myVesselId === vessel.vesselId,
          };
        } else {
          const sortedPoints = copyVesselsArray[vessel.vesselId].pointsArray
            .concat(vessel.pointsArray)
            .sort((a, b) => a.epoch - b.epoch);
          const uniquePoints = Array.from(
            new Map(sortedPoints.map((point) => [point.epoch, point])).values(),
          );
          copyVesselsArray[vessel.vesselId] = {
            ...copyVesselsArray[vessel.vesselId],
            pointsArray: uniquePoints,
          };
        }
        commit("SET_VESSEL_MAP_OBJECTS", copyVesselsArray);
      }
    }

    commit("SET_BUFFERED_PERCENT", 100);
  },

  async setStartPointVessels({ commit, getters }) {
    const startPointVessels = getters.getStartPointVessels;

    let pointFeatures = [];
    let polygonFeatures = [];
    startPointVessels.forEach((vessel) => {
      let currentPoint = vessel.pointsArray[0];
      let nextPoint = vessel.pointsArray[1];

      const geometry = {
        type: "Point",
        coordinates: [currentPoint.lon, currentPoint.lat],
      };
      let timeDiff = 0;
      if (nextPoint) {
        timeDiff = nextPoint.epoch - currentPoint.epoch;
      }
      const properties = {
        vesselName: vessel.vesselName,
        mmsi: vessel.mmsi,
        icon: VesselUtil.getSymbolIconNameLightWeight(
          Number(vessel.vesselType),
          currentPoint.speed,
          timeDiff,
        ),
        speed: currentPoint.speed,
        heading: currentPoint.heading,
        hasSize: vessel.hasSize,
      };
      pointFeatures.push(turf.feature(geometry, properties));

      if (vessel.hasSize) {
        let actualVessel = VoyageHistoryUtil.createPolygonFeature(
          vessel.vesselId,
          {
            lon: currentPoint.lon,
            lat: currentPoint.lat,
            h: currentPoint.heading,
          },
          vessel.polygonData,
        );
        polygonFeatures = polygonFeatures.concat(actualVessel);
      }
    });

    let pointCollection = turf.featureCollection(pointFeatures);
    let polygonCollection = turf.featureCollection(polygonFeatures);

    commit("SET_START_POINT_GEO_JSON", {
      point: pointCollection,
      polygon: polygonCollection,
    });
  },

  async fetchCameraList({ commit }, vesselId) {
    let response = await AiseaApiFactory.get("enterpriseVessel").fetchCameras(
      vesselId,
    );
    commit("SET_CAMERA_LIST", response.data.cameras);

    return response;
  },

  isBufferEnoughToResume: ({ state }, bufferedTime) => {
    if (bufferedTime - state.current_time > 60 || state.end_time == bufferedTime) {
      return true;
    } else {
      return false;
    }
  },

  isCurrentTimeAlreadyBuffered: ({ state }, t) => {
    return state.current_time < t;
  },

  isOnVoyageHistoryPlayerPage: ({ state }) => {
    return state.start_time != null;
  },

  initPlayer: ({ commit, state, dispatch }) => {
    let myVessel = state.vesselMapObjects[state.myVesselId];
    let startTimeUnix = myVessel.pointsArray[0].epoch;
    let endTimeUnix = myVessel.pointsArray[myVessel.pointsArray.length - 1].epoch;
    commit("SET_PLAY_BUCK_TIME_RANGE", {
      startTime: startTimeUnix,
      endTime: endTimeUnix,
    });
    commit("SET_CURRENT_TIME", startTimeUnix);

    createjs.Ticker.removeAllEventListeners("tick");
    createjs.Ticker.reset();
    createjs.Ticker.framerate = 1;
    createjs.Ticker.paused = state.is_pause;
    createjs.Ticker.addEventListener("tick", function (event) {
      if (!event.paused) {
        let ct = state.current_time;
        if (ct >= state.end_time) {
          dispatch("playTicker", false);
          return;
        }
        let now = ct + 1;

        commit("SET_CURRENT_TIME", now);
      }
    });
    createjs.Ticker.timingMode = createjs.Ticker.RAF_SYNCHED;
  },

  playTicker: ({ commit }, isPlay) => {
    let isPause = !isPlay;
    createjs.Ticker.paused = isPause;
    commit("SET_PAUSE", isPause);
  },

  setFPS: ({ commit }, fps) => {
    createjs.Ticker.framerate = fps;
    commit("SET_FPS", fps);
  },

  setCurrentTime: ({ commit }, time) => {
    commit("SET_CURRENT_TIME", parseInt(time));
  },
  clear: ({ commit }) => {
    createjs.Ticker.removeAllEventListeners("tick");
    commit("CLEAR");
  },

  /*=========================================================================================
  初期化
  ==========================================================================================*/
  async resetVoyageHistoryState({ commit }) {
    let defaultState = {
      vessel_id: null,
      my_points: [],
      area_points: List(),
      area_vessels: {},
      current_time: null,
      start_time: null,
      end_time: null,
      is_pause: true,
      fps: 1,
      camera_list: [],
      buffer_start_time: {},
      myVesselId: null,
      myTracks: null,
      vesselMapObjects: {},
      chunkPoints: [],
      startPointGeoJson: null,
    };
    commit("RESET_STATE", defaultState);
  },
};
